import {
  useProgress,
  setSourceLang,
  setDailyGoal,
  setMusicEnabled,
  setMusicVolume,
  setSpeakingEnabled,
  resetAll,
} from '../store/progress.js';
import { t } from '../i18n.js';
import { ChunkyButton, GhostButton } from './Buttons.jsx';
import { Icons } from './Icons.jsx';
import { BALL } from '../theme.js';
import { Sentry } from '../sentry.js';

const T = BALL;

function Section({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{
        fontSize: 11, fontWeight: 900, color: T.inkSoft,
        letterSpacing: '0.12em', textTransform: 'uppercase',
      }}>{label}</div>
      {children}
    </div>
  );
}

function Seg({ value, options, onPick }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onPick(o.value)}
            style={{
              flex: 1, appearance: 'none', cursor: 'pointer',
              background: active ? T.primary : T.bg,
              border: `2px solid ${active ? T.primaryDeep : T.hairline}`,
              color: active ? '#fff' : T.inkSoft,
              borderRadius: 12, padding: 10,
              fontWeight: 800, fontSize: 14, fontFamily: 'inherit',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Settings({ onClose }) {
  const progress = useProgress();
  const lang = progress.sourceLang;

  function handleReset() {
    if (confirm(lang === 'en'
        ? 'Reset all progress? This wipes XP, streak, and lessons.'
        : 'Återställ all framgång? Detta nollställer XP, streak och lektioner.')) {
      resetAll();
    }
  }

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      zIndex: 60,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: T.bgAlt, width: '100%', maxWidth: 480,
        borderRadius: '20px 20px 0 0',
        maxHeight: '85vh', overflowY: 'auto',
        padding: `12px 18px calc(env(safe-area-inset-bottom) + 18px)`,
        display: 'flex', flexDirection: 'column', gap: 18,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0 0 4px' }}>
          <div style={{ width: 40, height: 4, background: T.hairline, borderRadius: 2 }} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 22, fontWeight: 900, color: T.ink }}>
            {lang === 'en' ? 'Settings' : 'Inställningar'}
          </h2>
          <button type="button" onClick={onClose} style={{
            appearance: 'none', background: 'none', border: 'none', cursor: 'pointer',
          }}>
            {Icons.close(T.inkSoft)}
          </button>
        </div>

        <Section label={lang === 'en' ? 'Source language' : 'Källspråk'}>
          <Seg
            value={progress.sourceLang}
            onPick={setSourceLang}
            options={[
              { value: 'sv', label: lang === 'en' ? 'Swedish' : 'Svenska' },
              { value: 'en', label: lang === 'en' ? 'English' : 'Engelska' },
            ]}
          />
        </Section>

        <Section label={lang === 'en' ? 'Daily goal (XP)' : 'Dagligt mål (XP)'}>
          <Seg
            value={progress.dailyGoalXP}
            onPick={setDailyGoal}
            options={[10, 30, 50, 100].map((v) => ({ value: v, label: String(v) }))}
          />
        </Section>

        <Section label={lang === 'en' ? 'Background music' : 'Bakgrundsmusik'}>
          <div style={{
            background: T.bg, borderRadius: 12, padding: 12,
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <label style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              fontSize: 14, color: T.ink, fontWeight: 700, cursor: 'pointer',
            }}>
              <span>{lang === 'en' ? 'Play tamburica music' : 'Spela tamburicamusik'}</span>
              <input
                type="checkbox"
                checked={progress.musicEnabled}
                onChange={(e) => setMusicEnabled(e.target.checked)}
                style={{ width: 20, height: 20, accentColor: T.primary, cursor: 'pointer' }}
              />
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: progress.musicEnabled ? 1 : 0.4 }}>
              <span style={{ fontSize: 12, color: T.inkSoft, minWidth: 56 }}>
                {lang === 'en' ? 'Volume' : 'Volym'}
              </span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={progress.musicVolume}
                onChange={(e) => setMusicVolume(parseFloat(e.target.value))}
                disabled={!progress.musicEnabled}
                style={{ flex: 1, accentColor: T.primary, cursor: 'pointer' }}
              />
              <span style={{ fontSize: 12, color: T.inkSoft, minWidth: 32, textAlign: 'right' }}>
                {Math.round(progress.musicVolume * 100)}%
              </span>
            </div>
          </div>
        </Section>

        <Section label={lang === 'en' ? 'Exercises' : 'Övningar'}>
          <label style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: T.bg, borderRadius: 12, padding: 12,
            fontSize: 14, color: T.ink, fontWeight: 700, cursor: 'pointer',
          }}>
            <span>{t('settingsSpeaking', lang)}</span>
            <input
              type="checkbox"
              checked={progress.speakingEnabled}
              onChange={(e) => setSpeakingEnabled(e.target.checked)}
              style={{ width: 20, height: 20, accentColor: T.primary, cursor: 'pointer' }}
            />
          </label>
        </Section>

        <Section label={lang === 'en' ? 'Stats' : 'Statistik'}>
          <div style={{
            background: T.bg, borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 6,
          }}>
            <Row label={lang === 'en' ? 'Total XP' : 'Total XP'} value={progress.xp} />
            <Row label={lang === 'en' ? 'Gems' : 'Ädelstenar'} value={progress.gems} />
            <Row label={lang === 'en' ? 'Best streak' : 'Bästa streak'} value={`${progress.bestStreak} 🔥`} />
            <Row label={lang === 'en' ? 'Lessons completed' : 'Lektioner klara'} value={progress.totalLessons} />
          </div>
        </Section>

        <Section label={lang === 'en' ? 'Reset' : 'Återställ'}>
          <GhostButton style={{ color: T.red, borderColor: '#FFB3B3', boxShadow: '0 3px 0 #FFB3B3' }} onClick={handleReset}>
            {lang === 'en' ? 'Reset all progress' : 'Återställ all framgång'}
          </GhostButton>
        </Section>

        {/* TEMP: remove once Sentry is verified to receive events. */}
        <Section label="Sentry test (temporary)">
          <GhostButton onClick={() => {
            Sentry.captureException(new Error('Ballingo Sentry test event ' + new Date().toISOString()));
            alert('Sentry test event sent. Check sentry.io within ~1 min.');
          }}>
            Send test event
          </GhostButton>
        </Section>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
      <span style={{ color: T.inkSoft }}>{label}</span>
      <strong style={{ color: T.ink, fontWeight: 900 }}>{value}</strong>
    </div>
  );
}
